// Schema Markup Utility for SEO
// Handles dynamic schema generation with error handling and URL management

/**
 * Generate Organization Schema
 * @param {Object} data - Organization data
 * @returns {Object} Schema markup
 */
export function generateOrganizationSchema(data = {}) {
  try {
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": data.name || "Meander Software",
      "url": data.url || "https://meander.software",
      "logo": data.logo || "https://meander.software/favicon.png",
      "description": data.description || "Leading software development company providing professional app and website development services",
      "foundingDate": data.foundingDate || "2020",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": data.country || "India",
        "addressLocality": data.city || "Mumbai",
        "addressRegion": data.region || "Maharashtra"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "email": data.email || "info@meander.software",
        "url": data.url || "https://meander.software/contact"
      },
      "sameAs": [
        "https://www.linkedin.com/company/meander-software",
        "https://twitter.com/meander_software",
        "https://www.facebook.com/meander.software"
      ]
    };
  } catch (error) {
    console.error('Error generating organization schema:', error);
    return null;
  }
}

/**
 * Generate WebSite Schema
 * @param {Object} data - Website data
 * @returns {Object} Schema markup
 */
export function generateWebSiteSchema(data = {}) {
  try {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": data.name || "Meander Software",
      "url": data.url || "https://meander.software",
      "description": data.description || "Leading software development company providing professional app and website development services",
      "potentialAction": {
        "@type": "SearchAction",
        "target": {
          "@type": "EntryPoint",
          "urlTemplate": "https://meander.software/search?q={search_term_string}"
        },
        "query-input": "required name=search_term_string"
      }
    };
  } catch (error) {
    console.error('Error generating website schema:', error);
    return null;
  }
}

/**
 * Generate SoftwareApplication Schema for App Detail Pages
 * @param {Object} appData - App data from GraphQL
 * @param {string} slug - App slug for URL generation
 * @returns {Object} Schema markup
 */
export function generateSoftwareApplicationSchema(appData, slug) {
  try {
    if (!appData || !slug) {
      console.warn('Missing app data or slug for software application schema');
      return null;
    }

    const baseUrl = 'https://meander.software';
    const appUrl = `${baseUrl}/project/${slug}`;
    const screenshotUrl = appData.screenshot_1?.url ? `${baseUrl}${appData.screenshot_1.url}` : "";
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "@id": `${appUrl}#software`,
      "name": appData.name || "App",
      "description": appData.description || "Professional app development solution",
      "url": appUrl,
      "image": screenshotUrl,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web, Android, iOS",
      "softwareVersion": "1.0",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      },
      "provider": {
        "@type": "Organization",
        "name": "Meander Software",
        "url": baseUrl
      },
      "creator": {
        "@type": "Organization",
        "name": "Meander Software",
        "url": baseUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "Meander Software",
        "url": baseUrl
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "28"
      }
    };

    // Add screenshot if available
    if (appData.screenshot_1?.url) {
      schema.screenshot = screenshotUrl;
    }

    // Add features if available
    if (appData.key_features && Array.isArray(appData.key_features)) {
      schema.featureList = appData.key_features.map(feature => feature.title || feature.name).join(', ');
    }

    // Add pricing information if available
    if (appData.pricing_plan) {
      schema.offers = {
        "@type": "Offer",
        "price": "Contact for pricing",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "description": "Custom pricing plans available"
      };
    }

    return schema;
  } catch (error) {
    console.error('Error generating software application schema:', error);
    return null;
  }
}

/**
 * Generate Product Schema for software sold as products
 * @param {Object} appData - App data
 * @param {string} slug - App slug
 * @returns {Object} Schema markup
 */
export function generateProductSchema(appData, slug) {
  try {
    if (!appData || !slug) return null;
    const baseUrl = 'https://meander.software';
    const appUrl = `${baseUrl}/project/${slug}`;
    const screenshotUrl = appData.screenshot_1?.url ? `${baseUrl}${appData.screenshot_1.url}` : "";

    const schema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "@id": `${appUrl}#product`,
      "name": appData.name || "Software Product",
      "image": screenshotUrl,
      "description": appData.description || "Premium software solution asset",
      "brand": {
        "@type": "Brand",
        "name": "Meander Software"
      },
      "offers": {
        "@type": "Offer",
        "url": appUrl,
        "priceCurrency": "USD",
        "price": "0",
        "availability": "https://schema.org/InStock"
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "ratingCount": "28"
      }
    };

    // Add features description
    if (appData.key_features && Array.isArray(appData.key_features)) {
      schema.description = `${schema.description}. Key features include: ${appData.key_features.map(f => f.title || f.name).join(', ')}.`;
    }

    if (appData.pricing_plan) {
      schema.offers = {
        "@type": "Offer",
        "url": appUrl,
        "priceCurrency": "USD",
        "price": "Contact for pricing",
        "availability": "https://schema.org/InStock",
        "description": "Custom pricing plans available"
      };
    }

    return schema;
  } catch (error) {
    console.error('Error generating product schema:', error);
    return null;
  }
}

/**
 * Generate Article Schema for Blog/Resource Pages
 * @param {Object} articleData - Article data
 * @param {string} slug - Article slug for URL generation
 * @returns {Object} Schema markup
 */
export function generateArticleSchema(articleData, slug) {
  try {
    if (!articleData || !slug) {
      console.warn('Missing article data or slug for article schema');
      return null;
    }

    const baseUrl = 'https://meander.software';
    const articleUrl = `${baseUrl}/resource-detail/${slug}`;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": articleData.title || "Article",
      "description": articleData.description || "Informative article about software development",
      "url": articleUrl,
      "author": {
        "@type": "Organization",
        "name": "Meander Software",
        "url": baseUrl
      },
      "publisher": {
        "@type": "Organization",
        "name": "Meander Software",
        "url": baseUrl,
        "logo": {
          "@type": "ImageObject",
          "url": `${baseUrl}/favicon.png`
        }
      },
      "datePublished": articleData.publishedAt || new Date().toISOString(),
      "dateModified": articleData.updatedAt || new Date().toISOString(),
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": articleUrl
      }
    };

    // Add image if available
    if (articleData.image?.url) {
      schema.image = {
        "@type": "ImageObject",
        "url": `${baseUrl}${articleData.image.url}`,
        "width": 1200,
        "height": 630
      };
    }

    // Add keywords if available
    if (articleData.keywords) {
      schema.keywords = articleData.keywords;
    }

    return schema;
  } catch (error) {
    console.error('Error generating article schema:', error);
    return null;
  }
}

/**
 * Generate Service Schema for Service Pages
 * @param {Object} serviceData - Service data
 * @param {string} slug - Service slug for URL generation
 * @returns {Object} Schema markup
 */
export function generateServiceSchema(serviceData, slug) {
  try {
    if (!serviceData || !slug) {
      console.warn('Missing service data or slug for service schema');
      return null;
    }

    const baseUrl = 'https://meander.software';
    const serviceUrl = `${baseUrl}/services/${slug}`;
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": serviceData.name || serviceData.title || "Service",
      "description": serviceData.description || "Professional service offering",
      "url": serviceUrl,
      "provider": {
        "@type": "Organization",
        "name": "Meander Software",
        "url": baseUrl
      },
      "serviceType": "Software Development",
      "areaServed": {
        "@type": "Country",
        "name": "Worldwide"
      }
    };

    // Add image if available
    if (serviceData.image?.url) {
      schema.image = `${baseUrl}${serviceData.image.url}`;
    }

    // Add offers if available
    if (serviceData.pricing) {
      schema.offers = {
        "@type": "Offer",
        "price": "Contact for pricing",
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock"
      };
    }

    return schema;
  } catch (error) {
    console.error('Error generating service schema:', error);
    return null;
  }
}

/**
 * Generate BreadcrumbList Schema
 * @param {Array} breadcrumbs - Array of breadcrumb objects
 * @returns {Object} Schema markup
 */
export function generateBreadcrumbSchema(breadcrumbs) {
  try {
    if (!breadcrumbs || !Array.isArray(breadcrumbs)) {
      console.warn('Invalid breadcrumbs data for breadcrumb schema');
      return null;
    }

    const baseUrl = 'https://meander.software';
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": `${baseUrl}${item.url}`
      }))
    };

    return schema;
  } catch (error) {
    console.error('Error generating breadcrumb schema:', error);
    return null;
  }
}

/**
 * Generate FAQ Schema
 * @param {Array} faqData - Array of FAQ objects
 * @returns {Object} Schema markup
 */
export function generateFAQSchema(faqData) {
  try {
    if (!faqData || !Array.isArray(faqData) || faqData.length === 0) {
      console.warn('Invalid FAQ data for FAQ schema');
      return null;
    }

    // Filter out invalid FAQ entries
    const validFaqs = faqData.filter(faq => 
      faq && 
      (faq.title || faq.question) && 
      (faq.description || faq.answer)
    );

    if (validFaqs.length === 0) {
      console.warn('No valid FAQ entries found for schema');
      return null;
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": validFaqs.map(faq => ({
        "@type": "Question",
        "name": faq.title || faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.description || faq.answer
        }
      }))
    };

    return schema;
  } catch (error) {
    console.error('Error generating FAQ schema:', error);
    return null;
  }
}

/**
 * Generate LocalBusiness Schema for Contact/About Pages
 * @param {Object} businessData - Business data
 * @returns {Object} Schema markup
 */
export function generateLocalBusinessSchema(businessData = {}) {
  try {
    const baseUrl = 'https://meander.software';
    
    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": businessData.name || "Meander Software",
      "description": businessData.description || "Leading software development company",
      "url": baseUrl,
      "telephone": businessData.phone || "+91-XXXXXXXXXX",
      "email": businessData.email || "info@meander.software",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": businessData.streetAddress || "Business Address",
        "addressLocality": businessData.city || "Mumbai",
        "addressRegion": businessData.region || "Maharashtra",
        "postalCode": businessData.postalCode || "400001",
        "addressCountry": businessData.country || "India"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": businessData.latitude || "19.0760",
        "longitude": businessData.longitude || "72.8777"
      },
      "openingHours": "Mo-Fr 09:00-18:00",
      "priceRange": "$$"
    };

    return schema;
  } catch (error) {
    console.error('Error generating local business schema:', error);
    return null;
  }
}

/**
 * Inject schema markup into the page
 * @param {Object|Array} schemas - Schema markup object(s)
 */
export function injectSchemaMarkup(schemas) {
  try {
    // Remove existing schema scripts
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Convert to array if single object
    const schemaArray = Array.isArray(schemas) ? schemas : [schemas];

    // Filter out null schemas
    const validSchemas = schemaArray.filter(schema => schema !== null);

    // Inject new schema scripts
    validSchemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema, null, 2);
      document.head.appendChild(script);
    });

    console.log(`Injected ${validSchemas.length} schema markup(s)`);
  } catch (error) {
    console.error('Error injecting schema markup:', error);
  }
}

/**
 * Generate comprehensive schema for home page
 * @returns {Array} Array of schema objects
 */
export function generateHomePageSchema() {
  try {
    const schemas = [];

    // Organization schema
    const orgSchema = generateOrganizationSchema();
    if (orgSchema) schemas.push(orgSchema);

    // Website schema
    const websiteSchema = generateWebSiteSchema();
    if (websiteSchema) schemas.push(websiteSchema);

    return schemas;
  } catch (error) {
    console.error('Error generating home page schema:', error);
    return [];
  }
}

/**
 * Generate comprehensive schema for app detail page
 * @param {Object} appData - App data
 * @param {string} slug - App slug
 * @returns {Array} Array of schema objects
 */
export function generateAppDetailSchema(appData, slug) {
  try {
    const schemas = [];

    // Software application schema
    const softwareSchema = generateSoftwareApplicationSchema(appData, slug);
    if (softwareSchema) schemas.push(softwareSchema);

    // Product schema
    const productSchema = generateProductSchema(appData, slug);
    if (productSchema) schemas.push(productSchema);

    // Breadcrumb schema
    const breadcrumbs = [
      { name: "Home", url: "/" },
      { name: "Projects", url: "/projects" },
      { name: appData?.name || "App", url: `/project/${slug}` }
    ];
    const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
    if (breadcrumbSchema) schemas.push(breadcrumbSchema);

    // FAQ schema if available
    if (appData?.faq && Array.isArray(appData.faq) && appData.faq.length > 0) {
      const faqSchema = generateFAQSchema(appData.faq);
      if (faqSchema) {
        schemas.push(faqSchema);
      }
    }

    // Local Business schema
    const businessData = {
      name: "Meander Software Private Limited",
      streetAddress: "Cabin 18, 11th Floor, TDI Business Centre",
      city: "Mohali",
      region: "Punjab",
      postalCode: "160062",
      country: "India",
      phone: "+91-XXXXXXXXXX",
      email: "info@meander.software"
    };
    const localBusinessSchema = generateLocalBusinessSchema(businessData);
    if (localBusinessSchema) {
      schemas.push(localBusinessSchema);
    }

    return schemas;
  } catch (error) {
    console.error('Error generating app detail schema:', error);
    return [];
  }
}

/**
 * Generate comprehensive schema for resource detail page
 * @param {Object} resourceData - Resource data
 * @param {string} slug - Resource slug
 * @returns {Array} Array of schema objects
 */
export function generateResourceDetailSchema(resourceData, slug) {
  try {
    const schemas = [];

    // Article schema
    const articleSchema = generateArticleSchema(resourceData, slug);
    if (articleSchema) schemas.push(articleSchema);

    // Breadcrumb schema
    const breadcrumbs = [
      { name: "Home", url: "/" },
      { name: "Resources", url: "/resources" },
      { name: resourceData?.title || "Resource", url: `/resource-detail/${slug}` }
    ];
    const breadcrumbSchema = generateBreadcrumbSchema(breadcrumbs);
    if (breadcrumbSchema) schemas.push(breadcrumbSchema);

    // FAQ schema if available
    if (resourceData?.faq && Array.isArray(resourceData.faq) && resourceData.faq.length > 0) {
      const faqSchema = generateFAQSchema(resourceData.faq);
      if (faqSchema) {
        schemas.push(faqSchema);
      }
    }

    return schemas;
  } catch (error) {
    console.error('Error generating resource detail schema:', error);
    return [];
  }
} 